import { useMemo } from "react";
import type { Transaction } from "@mysten/sui/transactions";
import { buildRevoke, buildKillDrain, buildPause, buildUnpause, buildSetActions, buildSetValueGuard } from "@altheia-xyz/sui";
import { api } from "./api";
import { loadAgent } from "./suiConfig";
import { useTxAction } from "./useTxAction";

/**
 * Owner controls for one agent, all through useTxAction so the UI shows tx
 * lifecycle (signing → pending → success/failed/rejected) and refetches on
 * success. Each action records to the audit ledger (D3) carrying the tx digest.
 *
 * The agent SECRET is never stored (D2) — the developer runs the agent in their
 * own loop. We keep only the OwnerCap + object ids (public) needed to act.
 */
export function useAgentActions(agentId: string, token: string | null, onChange?: () => void | Promise<void>) {
  const stored = useMemo(() => loadAgent(agentId), [agentId]);
  const { state, run, reset } = useTxAction();

  const record = (event_type: string, action_type: string, amount?: number, asset?: string) => async (digest: string) => {
    if (!token) return;
    try { await api.recordSuiAudit({ agent_id: agentId, decision: "info", event_type, action_type, amount, asset, tx_digest: digest }, token); } catch { /* best-effort */ }
  };

  const act = (build: () => Transaction, event_type: string, action_type: string, amount?: number, asset?: string) =>
    stored ? run(build, { audit: record(event_type, action_type, amount, asset), onSuccess: onChange }) : Promise.resolve({ ok: false as const });

  return {
    ready: !!stored,
    state,
    reset,
    revoke: () => act(() => buildRevoke(stored!.refs), "agent_revoked", "revoke"),
    pause: () => act(() => buildPause(stored!.refs), "agent_paused", "pause"),
    unpause: () => act(() => buildUnpause(stored!.refs), "agent_unpaused", "unpause"),
    recover: (ownerAddr: string, coinTypes?: string[], amount?: number, asset?: string) => act(() => buildKillDrain(stored!.refs, ownerAddr, coinTypes), "funds_recovered", "recover", amount, asset),
    setActions: (actionIds: number[]) => act(() => buildSetActions(stored!.refs, actionIds), "policy_updated", "edit_actions"),
    setValueGuard: (maxSlippageBps: number, baseScalar: number) => act(() => buildSetValueGuard(stored!.refs, maxSlippageBps, baseScalar), "policy_updated", "edit_slippage"),
  };
}
